import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Zap, BarChart3, Flame, Heart, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFlavour } from '../../context/FlavourContext';
import { useLang } from '../../context/LanguageContext';
import ProductBox from '../../components/ProductBox';
import FlavourSelector from '../../components/FlavourSelector';
import FlavourCTA from '../../components/FlavourCTA';
import { track } from '../../lib/analytics';

const BENEFITS = [
  { icon: Zap, label: 'ENERGY YOU FEEL' },
  { icon: BarChart3, label: 'CONFIDENCE THAT SHOWS' },
  { icon: Flame, label: 'PERFORMANCE WITHOUT LIMITS' },
  { icon: Heart, label: 'ADDICTIVE TASTE' },
];

// energy spark field (positions are stable per mount)
function useSparks(count = 16) {
  return useRef(
    Array.from({ length: count }).map(() => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: 2 + Math.random() * 5,
      dur: 2.4 + Math.random() * 3.2,
      delay: Math.random() * 4,
      depth: 20 + Math.random() * 70,
      twinkle: Math.random() > 0.55,
    }))
  ).current;
}

export default function Hero() {
  const { flavour, pulse, next, prev, order, flavourId } = useFlavour();
  const { t } = useLang();
  const navigate = useNavigate();
  const touchX = useRef(null);
  const [anim, setAnim] = useState(false);
  const [par, setPar] = useState({ x: 0, y: 0 });
  const [drag, setDrag] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const sparks = useSparks(16);

  useEffect(() => { setAnim(true); const tm = setTimeout(() => setAnim(false), 650); return () => clearTimeout(tm); }, [pulse]);

  const onMove = useCallback((e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    setPar({ x, y });
  }, []);
  const onLeave = useCallback(() => setPar({ x: 0, y: 0 }), []);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; setSwiping(true); };
  const onTouchMove = (e) => {
    if (touchX.current == null) return;
    const dx = e.touches[0].clientX - touchX.current;
    // light resistance so it feels elastic
    setDrag(Math.sign(dx) * Math.min(Math.abs(dx) * 0.85, 200));
  };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 55) { dx < 0 ? next() : prev(); track('product_swiped', { flavour: flavourId, dir: dx < 0 ? 'next' : 'prev' }); }
    touchX.current = null;
    setSwiping(false);
    setDrag(0);
  };

  const idx = order.indexOf(flavourId);
  const px = (d) => `translate3d(${par.x * d}px, ${par.y * d}px, 0)`;

  return (
    <section
      className="relative min-h-[100svh] flex flex-col justify-center pt-28 pb-16 md:pt-24 overflow-hidden"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {/* ===== cinematic background ===== */}
      <div className="flavour-transition absolute inset-0 -z-30" style={{ background: `radial-gradient(ellipse 65% 60% at 72% 42%, rgba(${flavour.rgb},0.30), transparent 68%), linear-gradient(180deg, #050505, #0a0a0a 60%, #050505)` }} />
      {/* drifting grid */}
      <div className="absolute inset-0 -z-20 opacity-[0.05] grid-drift" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      {/* rotating conic aura behind product */}
      <div className="hidden md:block absolute -z-20 spin-slow" style={{ top: '50%', left: '72%', width: 700, height: 700, transform: 'translate(-50%,-50%)', background: `conic-gradient(from 0deg, transparent, rgba(${flavour.rgb},0.14), transparent 55%)`, borderRadius: '50%', filter: 'blur(6px)' }} />
      {/* big soft orb */}
      <div className="flavour-transition glow-pulse absolute -z-20 rounded-full blur-3xl" style={{ top: '38%', left: '72%', width: 480, height: 480, transform: 'translate(-50%,-50%)', background: `radial-gradient(circle, rgba(${flavour.rgb},0.55), transparent 65%)` }} />
      {/* vignette */}
      <div className="absolute inset-0 -z-10 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.7))' }} />

      <div className="max-w-[1400px] mx-auto w-full px-5 grid lg:grid-cols-2 gap-8 items-center relative z-10">
        {/* LEFT */}
        <div className="order-2 lg:order-1 text-center lg:text-left min-w-0 w-full" style={{ transform: px(-8) }}>
          <div className="inline-flex items-center gap-2 text-xs font-cond tracking-[0.25em] text-white/70 mb-5 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03]">
            <Zap className="w-4 h-4" style={{ color: flavour.primary }} fill={flavour.primary} /> NEW GENERATION FORMULA
          </div>
          <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl xl:text-8xl relative">
            FEEL THE<br />
            <span className="flavour-transition flavour-text-glow" style={{ color: flavour.primary }}>ENERGY</span><br />
            NOT JUST THE<br />DIFFERENCE
            <span className="font-script text-3xl md:text-4xl text-white/70 absolute -right-2 md:right-10 -bottom-6 hidden sm:block" style={{ color: flavour.primary }}>a bolder you.</span>
          </h1>
          <p className="mt-10 text-white/70 text-lg max-w-md mx-auto lg:mx-0">
            Confidence. Attraction. Performance.<br />In one daily experience.
          </p>

          {/* social proof */}
          <div className="mt-5 flex items-center gap-3 justify-center lg:justify-start">
            <div className="flex">{[0,1,2,3,4].map((i)=>(<Star key={i} className="w-4 h-4" color="#FFC61A" fill="#FFC61A" />))}</div>
            <span className="text-sm text-white/50 font-cond tracking-wide">RATED 4.8/5 BY 1,200+ MEN</span>
          </div>

          <div className="mt-7 grid grid-cols-2 sm:flex sm:flex-wrap gap-x-6 gap-y-4 max-w-md mx-auto lg:mx-0">
            {BENEFITS.map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-left">
                <b.icon className="w-5 h-5 shrink-0" style={{ color: flavour.primary }} />
                <span className="font-cond tracking-wide text-[11px] leading-tight text-white/70">{b.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
            <FlavourCTA onClick={() => { track('hero_cta_clicked', { flavour: flavourId }); navigate('/flavours'); }} />
            <span className="text-white/40 text-sm font-cond tracking-wide">{t('fastDelivery')}</span>
          </div>
        </div>

        {/* RIGHT product stage */}
        <div className="order-1 lg:order-2 flex flex-col items-center min-w-0 w-full">
          <div
            className="relative select-none w-full max-w-[580px] aspect-square flex items-center justify-center"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* orbital rings */}
            <div className="hidden sm:block absolute inset-[4%] rounded-full spin-slow flavour-transition" style={{ border: `1px solid rgba(${flavour.rgb},0.28)` }} />
            <div className="hidden sm:block absolute inset-[16%] rounded-full spin-rev flavour-transition" style={{ border: `1px dashed rgba(${flavour.rgb},0.35)` }} />
            <div className="hidden sm:block absolute inset-[30%] rounded-full spin-slow flavour-transition" style={{ border: `1px solid rgba(${flavour.rgb},0.18)` }} />

            {/* energy spark field */}
            {sparks.map((s, i) => (
              <span
                key={i}
                className={`absolute rounded-full ${s.twinkle ? 'twinkle' : 'spark'}`}
                style={{
                  left: s.left, top: s.top, width: s.size, height: s.size,
                  background: flavour.primary,
                  boxShadow: `0 0 ${s.size * 2.5}px ${s.size}px rgba(${flavour.rgb},0.55)`,
                  animationDuration: `${s.dur}s`, animationDelay: `${s.delay}s`,
                  transform: px(s.depth),
                }}
              />
            ))}

            {/* faint lightning bolts */}
            <Zap className="absolute bolt-pulse" style={{ top: '12%', right: '6%', width: 40, height: 40, color: flavour.primary, animationDuration: '3.5s', transform: px(58) }} fill={flavour.primary} />
            <Zap className="absolute bolt-pulse" style={{ bottom: '14%', left: '4%', width: 30, height: 30, color: flavour.primary, animationDuration: '4.2s', animationDelay: '1s', transform: px(70) }} fill={flavour.primary} />

            {/* spotlight */}
            <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 42% 58% at 50% 45%, rgba(${flavour.rgb},0.20), transparent 70%)` }} />

            {/* electric pulse on switch */}
            {anim && (
              <div className="electric-pulse absolute left-1/2 top-0 -translate-x-1/2 h-full w-[3px] z-20" style={{ background: `linear-gradient(180deg, transparent, ${flavour.primary}, transparent)` }} />
            )}

            {/* product + reflection */}
            <div
              className="relative z-10"
              style={{
                transform: `translate3d(calc(${par.x * 14}px + ${drag}px), ${par.y * 14}px, 0) rotate(${drag * 0.02}deg)`,
                opacity: swiping ? Math.max(0.5, 1 - Math.abs(drag) / 320) : 1,
                transition: swiping ? 'none' : 'transform 420ms cubic-bezier(0.22,1,0.36,1), opacity 300ms ease',
              }}
            >
              <div className="float-slow">
                <div style={{ transition: 'opacity 550ms cubic-bezier(0.22,1,0.36,1), transform 550ms cubic-bezier(0.22,1,0.36,1)' }}>
                  <ProductBox flavourId={flavourId} size="lg" />
                </div>
                {/* reflection */}
                <div className="pointer-events-none mt-1 opacity-25" style={{ transform: 'scaleY(-1)', maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent 55%)', WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent 55%)' }}>
                  <ProductBox flavourId={flavourId} size="lg" glow={false} />
                </div>
              </div>
              {/* light sweep across product */}
              <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                <div className="light-sweep absolute top-0 h-full w-1/3" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.14), transparent)' }} />
              </div>
            </div>
          </div>

          {/* mobile progress dots */}
          <div className="flex lg:hidden items-center gap-2 mt-2">
            {order.map((id, i) => (
              <span key={id} className="rounded-full transition-all duration-300" style={{ width: i === idx ? 22 : 8, height: 8, background: i === idx ? flavour.primary : 'rgba(255,255,255,0.25)' }} />
            ))}
          </div>
        </div>
      </div>

      {/* flavour selector */}
      <div className="absolute bottom-6 left-0 right-0 px-5 hidden md:block z-10">
        <div className="text-center mb-4">
          <h2 className="font-display text-2xl md:text-3xl">{t('chooseEnergy')}</h2>
          <p className="font-cond tracking-[0.2em] text-white/50 text-sm">{t('oneFormula')}</p>
        </div>
        <FlavourSelector source="hero" />
      </div>

      {/* mobile selector */}
      <div className="md:hidden w-full px-5 mt-6">
        <div className="text-center mb-4">
          <h2 className="font-display text-2xl">{t('chooseEnergy')}</h2>
          <p className="font-cond tracking-[0.2em] text-white/50 text-xs">{t('oneFormula')}</p>
        </div>
        <FlavourSelector source="hero-mobile" size="sm" />
      </div>
    </section>
  );
}
